import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from '@hooks/useDebounce';

import { usePayers } from '../store/hooks';
import type { Payer } from '../types';

import { StediPayerSearch, type StediPayerSearchProps } from './StediPayerSearch';

export interface PayerDirectorySearchProps extends Omit<
  StediPayerSearchProps,
  'error' | 'loading' | 'onInputChange' | 'onSelect' | 'options'
> {
  onSelect: (payer: Payer | null) => void;
}

/** Provider-directory container retained for current consumers. */
export function PayerDirectorySearch({ onSelect, value, ...props }: PayerDirectorySearchProps) {
  const [search, setSearch] = useState('');

  useEffect(() => {
    setSearch(value?.displayName ?? '');
  }, [value?.displayName, value?.stediId]);

  const debouncedSearch = useDebounce(search.trim(), 300);
  const { payers, isLoading, isFetching, isError } = usePayers(
    { search: debouncedSearch.length >= 2 ? debouncedSearch : null, perPage: 50 },
    { skip: debouncedSearch.length < 2 },
  );
  const options = useMemo(
    () =>
      payers.map(payer => ({
        avatarUrl: payer.avatar_url,
        displayName: payer.display_name,
        payerId: payer.primary_payer_id,
        stediId: payer.stedi_id,
      })),
    [payers],
  );
  const payerByStediId = useMemo(
    () => new Map(payers.map(payer => [payer.stedi_id, payer])),
    [payers],
  );

  return (
    <StediPayerSearch
      {...props}
      error={isError}
      inputValue={search}
      loading={isLoading || isFetching}
      onInputChange={setSearch}
      onSelect={option => {
        setSearch(option?.displayName ?? '');
        onSelect(option ? (payerByStediId.get(option.stediId) ?? null) : null);
      }}
      options={options}
      value={value}
    />
  );
}
